import { useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDialog, InlineName, MoveButtons, reorderPatches, TD, TH } from "@/components/admin/menu/shared";

// Radix Select cannot hold "" as an item value, so "no group" needs a token of its own.
const NO_GROUP = "none";

/**
 * Categories: the 30 headings every till groups its items under. Before this tab
 * they could only be picked from a dropdown — the server had create, rename,
 * re-sort, regroup and deactivate the whole time (DESIGN.md §10 V19).
 */
export default function MenuCategoriesTab({ data, readOnly }) {
  const { categories, groups } = data;
  const [name, setName] = useState("");
  const [groupId, setGroupId] = useState(NO_GROUP);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const rows = [...(categories || [])].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
  const activeGroups = (groups || []).filter(group => group.is_active);

  const add = async event => {
    event.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    const result = await data.createCategory({
      name: name.trim(),
      group_id: groupId === NO_GROUP ? null : Number(groupId),
      sort_order: (rows.length + 1) * 10,
    });
    setBusy(false);
    if (!result.ok) return toast.error(`Could not add category: ${result.error}`);
    toast.success(`Category "${name.trim()}" added`);
    setName("");
  };

  const rename = async (category, next) => {
    const result = await data.updateCategory(category.id, { name: next });
    if (!result.ok) toast.error(`Could not rename "${category.name}": ${result.error}`);
    else toast.success(`Renamed to "${next}"`);
    return result;
  };

  const regroup = async (category, value) => {
    const result = await data.updateCategory(category.id, { group_id: value === NO_GROUP ? null : Number(value) });
    if (!result.ok) toast.error(`Could not change the group of "${category.name}": ${result.error}`);
  };

  const move = async (index, delta) => {
    setBusy(true);
    const result = await data.reorderCategories(reorderPatches(rows, index, delta));
    setBusy(false);
    if (!result.ok) toast.error(`Could not reorder: ${result.error}`);
  };

  const setActive = async (category, active) => {
    const result = active
      ? await data.updateCategory(category.id, { is_active: true })
      : await data.deactivateCategory(category.id);
    if (!result.ok) return toast.error(`Could not ${active ? "reactivate" : "deactivate"} "${category.name}": ${result.error}`);
    // Tills hold the menu they loaded; there is no push for menu changes yet
    // (DESIGN.md §10 V20), so say when the change actually reaches them.
    toast.success(active
      ? `"${category.name}" is back — each till shows it the next time it loads the POS`
      : `"${category.name}" hidden — each till drops it the next time it loads the POS`);
  };

  if (!categories) return <p className="px-3 py-6 text-meta text-muted-foreground">Loading categories…</p>;

  return (
    <div className="space-y-3" data-testid="menu-categories-tab">
      {!readOnly && (
        <form className="flex flex-wrap items-end gap-2" onSubmit={add}>
          <label className="flex flex-col gap-1 text-meta font-medium">
            New category
            <Input className="h-8 w-56 text-body" value={name} onChange={event => setName(event.target.value)} placeholder="e.g. Mocktails" data-testid="menu-category-new-name" />
          </label>
          <label className="flex flex-col gap-1 text-meta font-medium">
            Group
            <GroupSelect value={groupId} onChange={setGroupId} groups={activeGroups} label="Group for the new category" testId="menu-category-new-group" />
          </label>
          <Button variant="action" type="submit" size="sm" disabled={busy || !name.trim()} data-testid="menu-category-add">
            <Plus className="h-4 w-4" />Add Category
          </Button>
        </form>
      )}

      <div className="overflow-x-auto rounded-md border bg-card">
        <table className="w-full">
          <thead className="border-b bg-muted/40">
            <tr>
              <th className={TH}>Name</th>
              <th className={TH}>Group</th>
              <th className={`${TH} text-right`}>Items</th>
              <th className={TH}>Order</th>
              <th className={TH}>Shown on tills</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((category, index) => (
              <tr key={category.id} className={category.is_active ? "" : "bg-muted/30"} data-testid={`menu-category-row-${category.id}`}>
                <td className={TD}>
                  <InlineName value={category.name} disabled={readOnly} label={`Name of ${category.name}`} onCommit={next => rename(category, next)} testId={`menu-category-name-${category.id}`} />
                </td>
                <td className={TD}>
                  <GroupSelect
                    value={category.group_id == null ? NO_GROUP : String(category.group_id)}
                    onChange={value => regroup(category, value)}
                    groups={(groups || []).filter(group => group.is_active || group.id === category.group_id)}
                    disabled={readOnly}
                    label={`Group of ${category.name}`}
                    testId={`menu-category-group-${category.id}`}
                  />
                </td>
                <td className={`${TD} text-right tabular-nums text-muted-foreground`}>{(category.items || []).length}</td>
                <td className={TD}>
                  <MoveButtons index={index} count={rows.length} onMove={move} disabled={readOnly || busy} testIdPrefix={`menu-category-move-${category.id}`} />
                </td>
                <td className={TD}>
                  <Switch
                    checked={category.is_active !== false}
                    disabled={readOnly}
                    aria-label={`Show ${category.name} on tills`}
                    onCheckedChange={next => (next ? setActive(category, true) : setConfirm(category))}
                    data-testid={`menu-category-active-${category.id}`}
                  />
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-meta text-muted-foreground">No categories yet. Add the first one above.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm ? `Hide "${confirm.name}" from every till?` : ""}
        body={confirm ? `Its ${(confirm.items || []).length} items come off billing and captain screens the next time each till loads the POS. Nothing is deleted — switch it back on here at any time.` : ""}
        confirmLabel="Hide Category"
        onCancel={() => setConfirm(null)}
        onConfirm={() => { const target = confirm; setConfirm(null); setActive(target, false); }}
        testId="menu-category-deactivate-dialog"
      />
    </div>
  );
}

function GroupSelect({ value, onChange, groups, disabled, label, testId }) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="h-8 w-44 text-body" aria-label={label} data-testid={testId}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NO_GROUP}>No group</SelectItem>
        {groups.map(group => (
          <SelectItem key={group.id} value={String(group.id)}>{group.name}{group.is_active ? "" : " (inactive)"}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
